import {
  Checkbox,
  ListItemText,
  MenuItem,
  Select,
} from '@mui/material';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

const CustomSelectRosterSelectUser = ({
  className = '',
  name,
  id,
  multiple = false,
  value = multiple ? [] : '',
  onChange = () => { },
  options,
  dataType,
  defaultValue,
  readOnly,
  position,
  onRemoveItem,
  searchable = true,
}) => {
  const { t } = useTranslation();
  const language = useSelector((state) => state.languageR.language);
  const containerRef = useRef(null);
  const [isMultiline, setIsMultiline] = useState(false);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;

    setIsMultiline(el.scrollHeight > el.clientHeight);
  }, [value]);

  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const searchRef = useRef(null);

  const normalizeText = (str = '') =>
    String(str)
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/đ/g, 'd')
      .replace(/Đ/g, 'D')
      .toLowerCase();

  const getValue = (option) => {
    switch (dataType) {
      case 'user':
        return option.userId;
      default:
        return option.value;
    }
  };

  const getLabel = (option) => {
    switch (dataType) {
      case 'user':
        return option.fullName;
      default:
        return option.label;
    }
  };

  const handleChange = (event) => {
    const { value } = event.target;
    onChange({
      target: {
        name,
        value: multiple
          ? (typeof value === 'string' ? value.split(',') : value)
          : value,
      },
    });
  };

  const rawOptions = Array.isArray(options) ? options : [];

  // ✅ Lọc theo query (không phân biệt hoa thường, có dấu vẫn match theo chuỗi)
  const filteredOptions = useMemo(() => {
    if (!searchable || !query.trim()) return rawOptions;

    const q = normalizeText(query);

    return rawOptions.filter((opt) =>
      normalizeText(getLabel(opt)).includes(q)
    );
  }, [rawOptions, searchable, query, dataType]);


  const validValue = multiple
    ? Array.isArray(value)
      ? value.filter((val) => rawOptions.some((option) => getValue(option) === val))
      : []
    : rawOptions.some((option) => getValue(option) === value)
      ? value
      : '';

  useEffect(() => {
    if (open && searchable && searchRef.current) {
      // nhỏ delay để menu mount xong
      const id = setTimeout(() => searchRef.current?.focus(), 50);
      return () => clearTimeout(id);
    }
  }, [open, searchable]);

  return (
    <Select
      multiple={multiple}
      name={name}
      id={id}
      value={validValue}
      onChange={handleChange}
      defaultValue={defaultValue || null}
      displayEmpty
      readOnly={readOnly}
      className={`form-control ${className}`}
      IconComponent={() => null}
      onOpen={() => setOpen(true)}
      onClose={() => {
        setOpen(false);
        setQuery(''); // đóng menu thì reset tìm kiếm (nếu muốn giữ thì bỏ dòng này)
      }}
      MenuProps={{
        anchorOrigin: {
          vertical: position === 'top' ? 'top' : 'bottom',
          horizontal: 'left',
        },
        transformOrigin: {
          vertical: position === 'top' ? 'bottom' : 'top',
          horizontal: 'left',
        },
        PaperProps: {
          style: { maxHeight: 320 }, // tăng tí để có chỗ cho ô search
        },
        // ⬅️ Quan trọng: đừng tự auto-focus item trong list
        MenuListProps: {
          autoFocusItem: false,
        },
      }}
      sx={{
        ...(dataType === 'user' && {
          backgroundColor: '#fff !important',
          border: '1px solid #E5E7EB !important',
          borderRadius: '6px !important',
          overflow: 'hidden',
          height: 'auto',
          minHeight: '40px',
          maxHeight: isMultiline ? '120px' : 'none',
          overflowY: isMultiline ? 'auto' : 'hidden',
          minHeight: '40px !important',
          boxSizing: 'border-box',
          '&.MuiInputBase-root': {
            height: 'auto !important',
            alignItems: validValue?.length === 0 ? 'center' : 'flex-start',
          },
          '& .MuiOutlinedInput-notchedOutline': {
            display: 'none !important',
          },
          '& .MuiSelect-icon': {
            display: 'none !important',
          },
          '& .MuiSelect-nativeInput': {
            display: 'none !important',
          },
          '& .MuiSelect-select': {
            display: 'flex',
            flexWrap: 'wrap',
            gap: '8px',
            alignItems: 'flex-start',
            height: 'auto !important',

            paddingTop: validValue?.length === 0 ? '0px' : '4px',
            paddingBottom: validValue?.length === 0 ? '0px' : '4px',
          },
          '&.MuiInputBase-readOnly': {
            height: '40px !important',
            borderRadius: '6px !important',
            border: '1px solid #E5E7EB !important',
            backgroundColor: '#fff !important',
          },
          // Ẩn icon dropdown của Bootstrap
          '&.form-control': {
            backgroundImage: 'none !important',
            backgroundRepeat: 'no-repeat !important',
            backgroundPosition: 'right 0.75rem center !important',
            backgroundSize: '0 !important',
          },

          // Ẩn icon của MUI (backup)
          '& .MuiSelect-icon, & .MuiSelect-iconOutlined': {
            display: 'none !important',
          },
          '& .MuiSelect-endAdornment': {
            display: 'none !important',
          },
          '& .MuiInputAdornment-root': {
            display: 'none !important',
          },
          '& .MuiSelect-select.MuiSelect-select': {
            paddingLeft: '0 !important',
          },
          // Bỏ hiệu ứng hover/focus
          '&:hover, &.Mui-focused': {
            border: '1px solid #E5E7EB !important',
            boxShadow: 'none !important',
          },

        }),
      }}
      renderValue={(selected) => {
        if (Array.isArray(selected)) {
          const selectedLabels = selected
            .map((val) => {
              const foundOption = rawOptions.find((option) => getValue(option) === val);
              return foundOption
                ? { value: getValue(foundOption), label: getLabel(foundOption) }
                : null;
            })
            .filter(Boolean);

          return (
            <div
              ref={containerRef}
              style={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: '8px',
                maxHeight: isMultiline ? '120px' : 'none',
                overflowY: isMultiline ? 'auto' : 'visible',
              }}
            >
              {selectedLabels.map(({ value: val, label }, idx) => (
                <div
                  key={idx}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    backgroundColor: '#fff',
                    padding: '0 8px 0 12px',
                    height: '32px',
                    borderRadius: '8px',
                    fontSize: '0.875rem',
                    border: '1px solid #E5E7EB',
                  }}
                >
                  <span style={{ marginRight: '8px' }}>{label}</span>
                  <span
                    onMouseDown={(e) => e.stopPropagation()}
                    onClick={(e) => {
                      e.stopPropagation();
                      const newSelected = selected.filter((item) => item !== val);
                      onChange({
                        target: {
                          name,
                          value: newSelected,
                        },
                      });
                      if (onRemoveItem) onRemoveItem(val);
                    }}
                    style={{
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      width: '20px',
                      height: '20px',
                      borderRadius: '50%',
                      backgroundColor: '#EAECF0',
                      color: '#fff',
                      fontWeight: 'bold',
                      fontSize: '14px',
                      lineHeight: 1,
                    }}
                  >
                    ×
                  </span>
                </div>
              ))}
            </div>
          );
        }

        const foundOption = rawOptions.find((option) => getValue(option) === selected);
        return foundOption ? getLabel(foundOption) : selected;
      }}
    >
      {/* 🔎 Ô tìm kiếm (chỉ render khi searchable) */}
      {searchable && (
        <MenuItem
          disableRipple
          onMouseDown={(e) => e.stopPropagation()}
          onClick={(e) => e.stopPropagation()}
          onKeyDown={(e) => e.stopPropagation()}   // ⬅️ Quan trọng nhất
          sx={{ '&.MuiMenuItem-root': { cursor: 'default' } }}
        >
          <input
            ref={searchRef}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={t('common.search')}
            style={{
              width: '100%',
              padding: '8px 10px',
              borderRadius: 6,
              border: '1px solid #E5E7EB',
              outline: 'none',
            }}
            // ngăn Enter đóng menu
            onKeyDown={(e) => {
              e.stopPropagation();
              // Ngăn tất cả phím điều hướng làm select xử lý
              if (['ArrowUp', 'ArrowDown', 'Home', 'End', 'PageUp', 'PageDown'].includes(e.key)) {
                e.preventDefault();
              }
            }}
          />
        </MenuItem>
      )}

      {/* Danh sách đã lọc */}
      {filteredOptions.map((option, index) => {
        const optVal = getValue(option);
        const label = getLabel(option);
        return (
          <MenuItem key={index} value={optVal} disableRipple
            sx={{
              "&:hover, &.Mui-focused, &.Mui-selected, &.Mui-selected:hover, &.Mui-focusVisible": {
                backgroundColor: "transparent !important",
              },
            }}
          >
            {multiple && (
              <Checkbox checked={Array.isArray(value) && value.indexOf(optVal) > -1} />
            )}
            <ListItemText primary={label} />
          </MenuItem>
        );
      })}

      {/* Không có dữ liệu */}
      {filteredOptions.length === 0 && (
        <MenuItem disabled>
          <ListItemText primary={t('common.no-data') || 'Không có dữ liệu'} />
        </MenuItem>
      )}
    </Select>
  );
};

export default CustomSelectRosterSelectUser;
